import {
  APPROVAL_STEP_TYPES,
  APPROVER_TYPE_KINDS,
  type ApprovalConditions,
  type ApproverSource,
} from '@clockwise/shared';
import { z } from 'zod';

// Re-export shared constants + type aliases so callers of this package
// have one import surface for the policy types. The Zod schemas below
// use the shared constants as their enum source — when a new approver
// source kind or step type is added in `@clockwise/shared`, this
// module's schemas pick it up automatically.
export type { ApprovalConditions, ApproverSource };

export const ApproverSourceSchema: z.ZodType<ApproverSource> = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('user'), userId: z.string().uuid() }),
  z.object({ kind: z.literal('system_role'), roleId: z.string().uuid() }),
  z.object({
    kind: z.literal('type'),
    type: z.enum(APPROVER_TYPE_KINDS),
  }),
]);

export const ApprovalStepSchema = z.object({
  approvalType: z.enum(APPROVAL_STEP_TYPES),
  approverSources: z.array(ApproverSourceSchema).min(1),
});
export type ApprovalStep = z.infer<typeof ApprovalStepSchema>;

const ConditionItemSchema = z.object({
  field: z.string(),
  op: z.enum(['eq', 'neq', 'in', 'not_in', 'gt', 'gte', 'lt', 'lte', 'between']),
  value: z.unknown(),
});
export const ApprovalConditionsSchema: z.ZodType<ApprovalConditions | null> = z
  .object({
    logic: z.enum(['all', 'any']),
    items: z.array(ConditionItemSchema).min(1),
  })
  .nullable();

export const ApprovalPolicySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  conditions: ApprovalConditionsSchema,
  steps: z.array(ApprovalStepSchema),
  // True when the policy bypasses the multi-step workflow and approves
  // matching submissions immediately. Implies `steps.length === 0`.
  autoApprove: z.boolean(),
  // Admin-controlled precedence within the tenant: lower wins on first-
  // match. The list page renders policies in this order; drag-reorder
  // POSTs the new ordering to /reorder.
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string().uuid().nullable(),
  updatedBy: z.string().uuid().nullable(),
});
export type ApprovalPolicy = z.infer<typeof ApprovalPolicySchema>;

const PolicyBody = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(1000).nullable().optional(),
  conditions: ApprovalConditionsSchema.optional(),
  // Allow 0 — the server's refine catches the auto-approve invariant
  // with a field-pathed error. Client-side rejection happens in the
  // form before this schema runs.
  steps: z.array(ApprovalStepSchema).min(0),
  autoApprove: z.boolean().default(false),
  isActive: z.boolean().default(true),
});

export const CreateApprovalPolicyInput = PolicyBody;
export type CreateApprovalPolicyInput = z.infer<typeof CreateApprovalPolicyInput>;

export const PatchApprovalPolicyInput = PolicyBody.partial();
export type PatchApprovalPolicyInput = z.infer<typeof PatchApprovalPolicyInput>;

export interface ListApprovalPoliciesQuery {
  q?: string;
  isActive?: boolean;
  sortField?: 'name' | 'sortOrder' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface ReorderApprovalPoliciesInput {
  orderedIds: string[];
}
